import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Loader, ArrowLeft, Printer, CheckCircle, TrendingUp, TrendingDown, ChevronRight, FileText, X,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import api from "../lib/api";
import { fmt, round1 } from "../lib/utils";
import KpiCard from "./common/KpiCard";
import SectionLabel from "./common/SectionLabel";

// Tema-tokens (samme som resten av appen): neg = #DC2626, pos = #16A34A
const LOGO_RED = "#DC2626";
const LOGO_GREEN = "#16A34A";

function monthShort(ym: string) {
  const [y, m] = (ym || "").split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("nb-NO", { month: "short" });
}
function monthLong(ym: string) {
  const [y, m] = (ym || "").split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("nb-NO", { month: "long", year: "numeric" });
}

function monthRange(startYM: string, endYM: string) {
  const [sy, sm] = startYM.split("-").map(Number);
  const [ey, em] = endYM.split("-").map(Number);
  const out: string[] = []; let y = sy, mo = sm;
  while ((y < ey || (y === ey && mo <= em)) && out.length < 60) {
    out.push(`${y}-${String(mo).padStart(2, "0")}`); mo++; if (mo === 13) { mo = 1; y++; }
  }
  return out;
}

function DeviationReport({ items, t, currency }: { items: any[]; t: any; currency: string }) {
  const [range, setRange] = useState<string>("12");
  const series = useMemo(() => {
    const keys = items.map((d: any) => (d.date || "").slice(0, 7)).filter(Boolean).sort();
    if (!keys.length) return [];
    const agg: Record<string, any> = {};
    items.forEach((d: any) => {
      const k = (d.date || "").slice(0, 7); if (!k) return;
      (agg[k] ||= { flagged: 0, count: 0, resolved: 0 });
      agg[k].flagged += d.nok_impact || 0; agg[k].count++;
      if (d.status === "resolved") agg[k].resolved += d.nok_impact || 0;
    });
    return monthRange(keys[0], keys[keys.length - 1]).map(mo => ({ month: mo, flagged: agg[mo]?.flagged || 0, count: agg[mo]?.count || 0, resolved: agg[mo]?.resolved || 0 }));
  }, [items]);

  if (series.length < 2) return null;

  const ranges = [{ k: "3", n: 3 }, { k: "6", n: 6 }, { k: "12", n: 12 }, { k: "max", n: series.length }];
  const n = range === "max" ? series.length : Math.min(Number(range), series.length);
  const visible = series.slice(-n);
  const total = visible.reduce((s, mo) => s + (mo.flagged || 0), 0);
  const count = visible.reduce((s, mo) => s + (mo.count || 0), 0);
  const avg = visible.length ? total / visible.length : 0;
  const prev = series.slice(Math.max(0, series.length - 2 * n), series.length - n);
  const prevTotal = prev.reduce((s, mo) => s + (mo.flagged || 0), 0);
  const deltaPct = prev.length === n && prevTotal > 0 ? round1((total - prevTotal) / prevTotal * 100) : null;

  return (
    <div className="cx-card p-5 sm:p-6 h-full">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <p className="text-[12.5px] font-medium text-muted">{t("analytics.dev_trend")}</p>
          <p className="text-[30px] sm:text-[34px] font-bold tabular leading-none mt-2 text-night">{fmt(total, currency)}</p>
          <p className="text-[12.5px] text-faint mt-2.5">{t("analytics.dev_period", { count, months: visible.length })}</p>
        </div>
        <div className="flex flex-col items-end gap-2.5">
          {deltaPct !== null && (
            <div className="text-right">
              <span className="text-[15px] font-bold tabular inline-flex items-center gap-0.5" style={{ color: deltaPct > 0 ? LOGO_RED : LOGO_GREEN }}>
                {deltaPct > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}{deltaPct > 0 ? "+" : ""}{deltaPct}%
              </span>
              <p className="text-[10.5px] text-faint mt-0.5">{t("analytics.vs_prev")}</p>
            </div>
          )}
          <div className="inline-flex items-center gap-0.5 bg-canvas border border-line rounded-[10px] p-0.5 no-print">
            {ranges.map(r => (
              <button key={r.k} onClick={() => setRange(r.k)} disabled={r.k !== "max" && r.n > series.length}
                className={`text-[12px] font-medium px-2.5 py-1 rounded-[8px] transition-colors disabled:opacity-30 disabled:cursor-default ${range === r.k ? "bg-night text-white" : "text-muted hover:text-night"}`}>
                {r.k === "max" ? t("analytics.range_max") : `${r.n}M`}
              </button>
            ))}
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={210}>
        <AreaChart data={visible} margin={{ top: 12, right: 8, left: 8, bottom: 0 }}>
          <defs><linearGradient id="devg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#18181B" stopOpacity={0.13} /><stop offset="100%" stopColor="#18181B" stopOpacity={0} /></linearGradient></defs>
          <XAxis dataKey="month" tickFormatter={monthShort} tick={{ fontSize: 11, fill: "#A1A1AA" }} axisLine={false} tickLine={false} minTickGap={16} />
          <YAxis hide domain={[0, (max: number) => max * 1.18]} />
          <Tooltip cursor={{ stroke: "#ECECEC", strokeWidth: 1 }} content={({ active, payload, label }: any) => active && payload?.length ? (
            <div className="bg-night text-white rounded-[12px] px-3.5 py-2.5 text-[12px] shadow-pop min-w-[160px]">
              <div className="font-semibold mb-2 capitalize">{monthLong(label)}</div>
              <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-white" /><span className="text-white/65">{t("analytics.flagged_lc")}</span><span className="ml-auto font-semibold tabular">{fmt(payload[0]?.payload.flagged, currency)}</span></div>
              <div className="flex items-center gap-2 mt-1.5"><span className="w-1.5 h-1.5 rounded-full bg-white/40" /><span className="text-white/65">{t("analytics.deviations_lc")}</span><span className="ml-auto font-semibold tabular">{payload[0]?.payload.count}</span></div>
            </div>) : null} />
          <ReferenceLine y={avg} stroke="#D4D4D8" strokeDasharray="4 4" label={{ value: t("analytics.avg_short"), position: "insideTopLeft", fontSize: 10.5, fill: "#A1A1AA" }} />
          <Area type="monotone" dataKey="flagged" stroke="#18181B" strokeWidth={2.5} fill="url(#devg)" dot={false} activeDot={{ r: 4, fill: "#18181B", stroke: "#fff", strokeWidth: 2 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function PriceChart({ row, t, currency }: { row: any; t: any; currency: string }) {
  const up = row.delta_pct > 0;
  const color = up ? LOGO_RED : LOGO_GREEN;
  const gid = `pg-${row.item_id}`;
  return (
    <div className="border border-line rounded-[12px] p-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold text-night truncate">{row.name}</p>
          <p className="text-[11.5px] text-faint tabular">{fmt(row.contracted_unit_price, currency)} → {fmt(row.latest_unit_price, currency)}</p>
        </div>
        <span className="text-[14px] font-bold tabular inline-flex items-center gap-0.5 shrink-0" style={{ color }}>
          {up ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}{row.delta_pct}%
        </span>
      </div>
      <ResponsiveContainer width="100%" height={130}>
        <AreaChart data={row.sparkline} margin={{ top: 14, right: 14, left: 6, bottom: 0 }}>
          <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.16} /><stop offset="100%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
          <XAxis dataKey="date" tickFormatter={(d: string) => (d || "").slice(0, 7)} tick={{ fontSize: 10.5, fill: "#A1A1AA" }} axisLine={false} tickLine={false} minTickGap={24} />
          <YAxis hide domain={[(min: number) => Math.min(min, row.contracted_unit_price) * 0.97, (max: number) => Math.max(max, row.contracted_unit_price) * 1.03]} />
          <Tooltip cursor={{ stroke: "#ECECEC", strokeWidth: 1 }} content={({ active, payload, label }: any) => active && payload?.length ? (
            <div className="bg-night text-white rounded-[10px] px-3 py-2 text-[12px] shadow-pop">
              <div className="font-semibold">{(label || "").slice(0, 7)}</div><div className="tabular">{fmt(payload[0].value, currency)}</div>
            </div>) : null} />
          <ReferenceLine y={row.contracted_unit_price} stroke="#A1A1AA" strokeDasharray="4 4"
            label={{ value: t("analytics.contracted_price"), position: "insideBottomRight", fontSize: 10.5, fill: "#A1A1AA" }} />
          <Area type="monotone" dataKey="unit_price" stroke={color} strokeWidth={2.5} fill={`url(#${gid})`} dot={false} activeDot={{ r: 4, fill: color, stroke: "#fff", strokeWidth: 2 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function SupplierDetailView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { supplierKey } = useParams<{ supplierKey: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState<string>("all");
  const [tab, setTab] = useState<"oversikt" | "pris" | "avvik">("oversikt");
  const [devType, setDevType] = useState<string>("all");
  const [devStatus, setDevStatus] = useState<string>("all");

  useEffect(() => {
    setLoading(true);
    const q = contract === "all" ? "" : `?contract_id=${encodeURIComponent(contract)}`;
    api.get(`/portfolio/suppliers/${encodeURIComponent(supplierKey || "")}/analytics${q}`)
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [supplierKey, contract]);

  const drillToInvoice = (inv: any) =>
    navigate(`/invoices?${inv.project_id ? `project=${inv.project_id}&` : ""}invoice=${inv.invoice_id}${inv.linje_nr ? `&line=${inv.linje_nr}` : ""}`);

  // Slå sammen "udokumentert tillegg" + "manglende referanse" -> ett grunnlag
  const dispType = (tp: string) => (tp === "udokumentert_tillegg" || tp === "manglende_referanse") ? "uten_grunnlag" : tp;
  const dispLabel = (tp: string) => tp === "uten_grunnlag" ? t("analytics.type_no_basis") : t(`avvikstype.${tp}`, { defaultValue: tp });

  const deviations = data?.deviations?.items || [];
  const filteredDev = deviations.filter((d: any) => (devType === "all" || dispType(d.type) === devType) && (devStatus === "all" || d.status === devStatus));
  const typeStats = useMemo(() => {
    const merge = (tp: string) => (tp === "udokumentert_tillegg" || tp === "manglende_referanse") ? "uten_grunnlag" : tp;
    const m: Record<string, any> = {};
    deviations.forEach((d: any) => { const k = merge(d.type); (m[k] ||= { type: k, count: 0, sum: 0 }); m[k].count++; m[k].sum += d.nok_impact || 0; });
    return Object.values(m).sort((a: any, b: any) => b.sum - a.sum);
  }, [deviations]);
  const flaggedTotal = useMemo(() => deviations.reduce((s: number, d: any) => s + (d.nok_impact || 0), 0), [deviations]);
  const commonType: any = typeStats.length ? typeStats.reduce((a: any, b: any) => a.count >= b.count ? a : b) : null;

  const allPriceRows = useMemo(() => (data?.price_trends || []).flatMap((pt: any) =>
    (pt.rows || []).map((r: any) => ({ ...r, contract_id: pt.contract_id }))), [data]);
  const topPrice = useMemo(() => allPriceRows.reduce((b: any, r: any) =>
    (!b || Math.abs(r.delta_pct || 0) > Math.abs(b.delta_pct || 0)) ? r : b, null), [allPriceRows]);
  const overContract = allPriceRows.filter((r: any) => (r.delta_pct || 0) > 0).length;
  const avgDrift = allPriceRows.length ? round1(allPriceRows.reduce((s: number, r: any) => s + (r.delta_pct || 0), 0) / allPriceRows.length) : 0;

  if (loading) return <div className="flex justify-center py-32"><Loader className="w-6 h-6 animate-spin text-faint" /></div>;
  if (!data) return (
    <div className="cx-card p-12 text-center">
      <p className="text-[15px] font-bold text-night">{t("analytics.not_found")}</p>
      <button onClick={() => navigate("/suppliers")} className="cx-btn-ghost mt-4 !text-[13px]">{t("analytics.back")}</button>
    </div>
  );

  const sp = data.spend, dv = data.deviations, contracts = data.supplier.contracts || [];
  // Leverandør-nivå valuta (alle kontrakter per leverandør deler valuta) — leses ÉN gang
  // og sendes inn i hver fmt(...) + ned i underkomponentene (DeviationReport/PriceChart).
  const currency = data.supplier.currency || "NOK";
  const ivc = sp.invoiced_vs_contract;
  const contractMeta: Record<string, any> = Object.fromEntries(contracts.map((c: any) => [c.id, c]));
  const byProject: Record<string, any[]> = {};
  contracts.forEach((c: any) => { (byProject[c.project_name || "—"] ||= []).push(c); });
  const multiProject = Object.keys(byProject).length > 1;
  const priceByProject: Record<string, any[]> = {};
  (data.price_trends || []).forEach((pt: any) => { (priceByProject[contractMeta[pt.contract_id]?.project_name || "—"] ||= []).push(pt); });

  const pricingLabel = (pm: string) => pm === "unit_price" ? t("detail.pricing_unit") : pm === "fixed_sum_installments" ? t("detail.pricing_fixed") : pm === "cost_plus" ? t("detail.pricing_cost") : t("detail.pricing_unset");

  const sumOriginal = (dv.chains || []).reduce((s: number, c: any) => s + (c.root_total || 0), 0);
  const sumPaid = (dv.chains || []).reduce((s: number, c: any) => s + (c.head_total || 0), 0);
  const savingsRate = sumOriginal > 0 ? round1((dv.caught_value / sumOriginal) * 100) : 0;
  const avgPerChain = (dv.chains || []).length ? dv.caught_value / dv.chains.length : 0;
  const hasSavings = (dv.chains || []).length > 0;
  const hasTrend = new Set(deviations.map((d: any) => (d.date || "").slice(0, 7)).filter(Boolean)).size >= 2;
  const openCount = deviations.filter((d: any) => d.status !== "resolved").length;
  const resolvedCount = deviations.length - openCount;

  const CaughtTile = () => (
    <button onClick={() => setTab("avvik")} className="cx-card p-4 sm:p-5 text-left transition-all hover:shadow-pop hover:-translate-y-0.5">
      <p className="text-[12.5px] font-medium text-muted">{t("analytics.caught_value")}</p>
      <p className="text-metric tabular mt-2 text-night">{fmt(dv.caught_value, currency)}</p>
      <p className="text-[12.5px] text-faint mt-1.5 inline-flex items-center gap-0.5">{t("analytics.see_chains")} <ChevronRight className="w-3.5 h-3.5" /></p>
    </button>
  );

  const KpiRow = () => {
    if (tab === "pris") return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <KpiCard label={t("analytics.price_tracked")} value={allPriceRows.length} />
        <KpiCard label={t("analytics.price_over")} value={`${overContract}/${allPriceRows.length}`} />
        <KpiCard label={t("analytics.avg_drift")} value={`${avgDrift > 0 ? "+" : ""}${avgDrift}%`}
          delta={allPriceRows.length ? { label: `${avgDrift > 0 ? "+" : ""}${avgDrift}%`, direction: avgDrift >= 0 ? "up" : "down", tone: avgDrift >= 0 ? "neg" : "pos" } : null} />
        {topPrice ? <KpiCard label={t("analytics.largest_dev")} value={`${topPrice.delta_pct > 0 ? "+" : ""}${topPrice.delta_pct}%`} sub={topPrice.name} />
          : <KpiCard label={t("analytics.largest_dev")} value="—" />}
      </div>
    );
    if (tab === "avvik") return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <KpiCard label={t("analytics.caught_value")} value={fmt(dv.caught_value, currency)} sub={hasSavings ? `${savingsRate}% ${t("analytics.saved")}` : undefined} />
        <KpiCard label={t("analytics.flagged_total")} value={fmt(flaggedTotal, currency)} sub={t("analytics.deviations_count_sub", { count: deviations.length })} />
        <KpiCard label={t("analytics.open_deviations")} value={dv.open_count} sub={t("analytics.awaiting_sub", { count: dv.awaiting_correction })} />
        {commonType ? <KpiCard label={t("analytics.common_type")} value={dispLabel(commonType.type)} sub={t("analytics.deviations_count_sub", { count: commonType.count })} />
          : <KpiCard label={t("analytics.common_type")} value="—" />}
      </div>
    );
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <KpiCard label={t("analytics.spend_12m")} value={fmt(sp.total_12m, currency)}
          delta={sp.delta_pct !== null ? { label: `${sp.delta_pct > 0 ? "+" : ""}${sp.delta_pct}%`, direction: sp.delta_pct >= 0 ? "up" : "down", tone: sp.delta_pct >= 0 ? "pos" : "neg" } : null} />
        <KpiCard label={t("analytics.invoiced_of_contract")} value={ivc.pct !== null ? `${ivc.pct}%` : "—"} sub={`${fmt(ivc.remaining, currency)} ${t("analytics.remaining")}`} />
        <CaughtTile />
        {topPrice ? (
          <button onClick={() => setTab("pris")} className="cx-card p-4 sm:p-5 text-left transition-all hover:shadow-pop hover:-translate-y-0.5">
            <p className="text-[12.5px] font-medium text-muted">{t("analytics.price_vs_contract")}</p>
            <p className="text-metric tabular mt-2 inline-flex items-center gap-1" style={{ color: topPrice.delta_pct > 0 ? LOGO_RED : LOGO_GREEN }}>
              {topPrice.delta_pct > 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}{topPrice.delta_pct > 0 ? "+" : ""}{topPrice.delta_pct}%
            </p>
            <p className="text-[12.5px] text-faint mt-1.5 truncate">{topPrice.name}</p>
          </button>
        ) : <KpiCard label={t("analytics.price_vs_contract")} value="—" sub={t("analytics.price_na")} />}
      </div>
    );
  };

  const DeviationRow = ({ d }: { d: any }) => (
    <button onClick={() => drillToInvoice(d)} className="w-full grid grid-cols-[1fr_120px_120px_90px_90px] gap-3 px-4 py-3 items-center text-left border-b border-line last:border-0 hover:bg-black/[0.02] transition-colors">
      <div className="min-w-0">
        <span className="text-[13px] font-medium text-night">{dispLabel(dispType(d.type))}</span>
        {d.linje_nr != null && <p className="text-[11.5px] text-faint truncate">{t("analytics.line")} {d.linje_nr}{d.beskrivelse ? ` — ${d.beskrivelse}` : ""}</p>}
      </div>
      <span className="inline-flex items-center gap-1 text-[12.5px] text-info tabular truncate"><FileText className="w-3.5 h-3.5 shrink-0" />{d.invoice_number}</span>
      <span className="text-[13px] font-semibold tabular text-right" style={{ color: LOGO_RED }}>{fmt(d.nok_impact, currency)}</span>
      <span className={`text-[11.5px] font-medium ${d.status === "resolved" ? "text-pos" : "text-flag"}`}>{t(`analytics.${d.status}`)}</span>
      <span className="text-[12px] text-faint tabular text-right">{(d.date || "").slice(0, 10)}</span>
    </button>
  );

  return (
    <div className="animate-fade-up print-area">
      <button onClick={() => navigate("/suppliers")} className="no-print inline-flex items-center gap-1.5 text-[13px] text-muted hover:text-night transition-colors mb-3">
        <ArrowLeft className="w-4 h-4" /> {t("analytics.back")}
      </button>
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-greeting text-night truncate">{data.supplier.vendor_name}</h1>
          <p className="text-[13px] text-muted mt-1">
            {data.supplier.vendor_org_number ? `Org. ${data.supplier.vendor_org_number} · ` : ""}
            {t("suppliers.contracts_projects", { contracts: contracts.length, projects: Object.keys(byProject).length })}
          </p>
        </div>
        <div className="flex items-center gap-2 no-print">
          {contracts.length > 1 && (
            <select value={contract} onChange={e => setContract(e.target.value)} className="field-ios !py-2 !text-[13px] !w-auto max-w-[280px]">
              <option value="all">{t("analytics.all_contracts")}</option>
              {Object.entries(byProject).map(([pn, cs]) => (
                <optgroup key={pn} label={pn}>{(cs as any[]).map((c: any) => <option key={c.id} value={c.id}>{c.vendor_name}</option>)}</optgroup>
              ))}
            </select>
          )}
          <button onClick={() => window.print()} className="cx-btn-ghost !py-2 !text-[13px]"><Printer className="w-4 h-4" /> {t("analytics.export_pdf")}</button>
        </div>
      </div>

      <KpiRow />

      <div className="border-b border-line mb-6 flex gap-6 no-print">
        <button onClick={() => setTab("oversikt")} className={`tab-underline ${tab === "oversikt" ? "active" : ""}`}>{t("analytics.tab_overview")}</button>
        <button onClick={() => setTab("pris")} className={`tab-underline ${tab === "pris" ? "active" : ""}`}>{t("analytics.tab_price")}</button>
        <button onClick={() => setTab("avvik")} className={`tab-underline ${tab === "avvik" ? "active" : ""}`}>
          {t("analytics.tab_deviations")}{deviations.length > 0 && <span className="ml-1.5 text-[10px] font-bold" style={{ color: LOGO_RED }}>{deviations.length}</span>}
        </button>
      </div>

      {/* ── OVERSIKT ── */}
      <div className={tab === "oversikt" ? "" : "hidden print:block"}>
        {/* Kontraktsforbruk — klikkbare kort side ved side, åpner prosjektet */}
        <SectionLabel className="!mt-0">{t("analytics.consumption_section")}</SectionLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(data.contracts_breakdown || []).map((c: any) => (
            <button key={c.id} onClick={() => c.project_id && navigate(`/projects/${c.project_id}`)} disabled={!c.project_id}
              className="cx-card p-5 text-left transition-all hover:shadow-pop hover:-translate-y-0.5 disabled:hover:translate-y-0 disabled:hover:shadow-card disabled:cursor-default">
              <div className="flex items-start justify-between gap-2 mb-3">
                <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-faint leading-snug">{c.project_name || c.vendor_name || "—"}</p>
                {c.project_id && <ChevronRight className="w-4 h-4 text-faint shrink-0 mt-0.5" />}
              </div>
              <div className="flex items-baseline justify-between gap-2 mb-1.5">
                <span className="cx-chip bg-black/[0.05] text-muted">{pricingLabel(c.pricing_model)}</span>
                <p className={`text-[20px] font-bold tabular ${(c.pct || 0) > 100 ? "text-neg" : "text-night"}`}>{c.pct !== null ? `${c.pct}%` : "—"}</p>
              </div>
              <div className="h-2.5 rounded-full bg-black/[0.06] overflow-hidden"><div className={`h-full rounded-full ${(c.pct || 0) > 100 ? "bg-neg" : "bg-night"}`} style={{ width: `${Math.min(100, c.pct || 0)}%` }} /></div>
              <p className="text-[11.5px] text-faint tabular mt-2">{fmt(c.invoiced, currency)} {t("analytics.invoiced_lc")} {t("analytics.of")} {fmt(c.revised, currency)}{c.revised - c.invoiced > 0 ? ` · ${fmt(c.revised - c.invoiced, currency)} ${t("analytics.remaining")}` : ""}</p>
            </button>
          ))}
        </div>

        {/* Forbruk per måned */}
        <div className="cx-card p-5 mb-2 mt-6">
          <div className="flex items-baseline justify-between mb-1">
            <p className="text-[13px] font-semibold text-night">{t("analytics.monthly_spend")}</p>
            <p className="text-[12px] text-muted">{t("analytics.avg_per_month", { amount: fmt(sp.avg_monthly, currency) })}</p>
          </div>
          {sp.has_history ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={sp.monthly} margin={{ top: 10, right: 8, left: 8, bottom: 0 }}>
                <defs><linearGradient id="spendg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#18181B" stopOpacity={0.14} /><stop offset="100%" stopColor="#18181B" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="month" tickFormatter={monthShort} tick={{ fontSize: 11, fill: "#A1A1AA" }} axisLine={false} tickLine={false} minTickGap={16} />
                <Tooltip cursor={{ stroke: "#ECECEC" }} content={({ active, payload, label }: any) => active && payload?.length ? (
                  <div className="bg-night text-white rounded-[10px] px-3 py-2 text-[12px] shadow-pop"><div className="font-semibold">{label}</div><div className="tabular">{fmt(payload[0]?.payload.amount, currency)} · {payload[0]?.payload.count} {t("analytics.invoices")}</div></div>) : null} />
                <Area type="monotone" dataKey="amount" stroke="#18181B" strokeWidth={2} fill="url(#spendg)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-[13px] text-faint text-center py-10">{t("analytics.spend_empty")}</p>}
        </div>
      </div>

      {/* ── PRISTREND ── */}
      <div className={tab === "pris" ? "" : "hidden print:block"}>
        {(data.price_trends || []).length === 0 ? (
          <div className="cx-card p-10 text-center"><p className="text-[13px] text-faint">{t("analytics.price_na")}</p></div>
        ) : Object.entries(priceByProject).map(([pn, pts]) => (
          <div key={pn} className="mb-6">
            {multiProject && <SectionLabel className="!mt-0">{pn}</SectionLabel>}
            {(pts as any[]).map((pt: any) => {
              const rows = [...(pt.rows || [])].sort((a: any, b: any) => Math.abs(b.delta_pct || 0) - Math.abs(a.delta_pct || 0));
              return (
                <div key={pt.contract_id} className="cx-card p-5 mb-4">
                  <p className="text-[14px] font-semibold text-night mb-3">{pt.vendor_name}{pt.pricing_model && pt.pricing_model !== "unit_price" ? ` · ${pricingLabel(pt.pricing_model)}` : ""}</p>
                  {pt.needs_pricing_model ? (
                    <div className="flex items-center justify-between gap-3 bg-canvas border border-line rounded-[10px] px-4 py-3"><p className="text-[12.5px] text-muted">{t("analytics.set_pricing_model")}</p><ChevronRight className="w-4 h-4 text-faint" /></div>
                  ) : rows.length === 0 ? (
                    <p className="text-[13px] text-faint py-2">{t("analytics.price_empty")}</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">{rows.map((r: any) => <PriceChart key={r.item_id} row={r} t={t} currency={currency} />)}</div>
                      {pt.summary && <p className="text-[12.5px] text-muted leading-relaxed mt-3 flex items-start gap-2"><TrendingUp className="w-3.5 h-3.5 mt-0.5 shrink-0 text-faint" />{pt.summary}</p>}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* ── AVVIK ── */}
      <div className={tab === "avvik" ? "" : "hidden print:block"}>
        {deviations.length === 0 && dv.caught_value === 0 ? (
          <div className="cx-card p-10 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: "rgba(52,199,89,0.12)" }}><CheckCircle className="w-6 h-6" style={{ color: LOGO_GREEN }} /></div>
            <p className="text-[14px] font-semibold text-night">{t("analytics.no_deviations")}</p>
          </div>
        ) : (
          <>
            {/* Rad 1 — Flagget over tid (bred) + Spart med Contrack (smal) */}
            {(hasTrend || hasSavings) && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                {hasTrend && (
                  <div className={hasSavings ? "lg:col-span-2" : "lg:col-span-3"}>
                    <DeviationReport items={deviations} t={t} currency={currency} />
                  </div>
                )}
                {hasSavings && (
                  <div className={hasTrend ? "lg:col-span-1" : "lg:col-span-3"}>
                    <div className="cx-card p-5 sm:p-6 h-full flex flex-col">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[12.5px] font-medium text-muted">{t("analytics.savings_title")}</p>
                          <p className="text-[28px] sm:text-[30px] font-bold tabular leading-none mt-2 text-night">{fmt(dv.caught_value, currency)}</p>
                          <p className="text-[12.5px] text-faint mt-2">{t("analytics.savings_sub", { rate: savingsRate, original: fmt(sumOriginal, currency) })}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-[15px] font-bold tabular inline-flex items-center gap-0.5 text-pos"><TrendingDown className="w-4 h-4" />{savingsRate}%</span>
                          <p className="text-[10.5px] text-faint mt-0.5">{t("analytics.saved")}</p>
                        </div>
                      </div>
                      <div className="mt-5">
                        <div className="h-3 rounded-full bg-black/[0.06] overflow-hidden flex">
                          <div className="h-full bg-night" style={{ width: `${sumOriginal ? (sumPaid / sumOriginal) * 100 : 0}%` }} />
                          <div className="h-full bg-pos" style={{ width: `${sumOriginal ? (dv.caught_value / sumOriginal) * 100 : 0}%` }} />
                        </div>
                        <div className="flex items-center gap-x-4 gap-y-1 mt-2.5 text-[11.5px] flex-wrap">
                          <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-night" /><span className="text-muted">{t("analytics.actually_paid")}</span><span className="font-semibold tabular text-night">{fmt(sumPaid, currency)}</span></span>
                          <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-pos" /><span className="text-muted">{t("analytics.saved")}</span><span className="font-semibold tabular text-night">{fmt(dv.caught_value, currency)}</span></span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-5 pt-4 border-t border-line">
                        <div><p className="text-[16px] font-bold tabular leading-none text-night">{(dv.chains || []).length}</p><p className="text-[10.5px] text-faint mt-1.5">{t("analytics.corrected_chains")}</p></div>
                        <div><p className="text-[16px] font-bold tabular leading-none text-night">{fmt(avgPerChain, currency)}</p><p className="text-[10.5px] text-faint mt-1.5">{t("analytics.avg_per_chain")}</p></div>
                      </div>
                      <details className="mt-3 group">
                        <summary className="text-[11px] text-faint cursor-pointer hover:text-muted list-none inline-flex items-center gap-1"><ChevronRight className="w-3.5 h-3.5 group-open:rotate-90 transition-transform" />{t("analytics.caught_breakdown")}</summary>
                        <div className="mt-2 space-y-1.5 pl-1">
                          {dv.chains.map((ch: any, i: number) => (
                            <div key={i} className="text-[11.5px] text-muted tabular">
                              <button onClick={() => drillToInvoice({ invoice_id: ch.root_id, project_id: ch.root_project_id })} className="text-info hover:underline">{ch.root_number}</button>
                              {" → "}
                              <button onClick={() => drillToInvoice({ invoice_id: ch.head_id, project_id: ch.head_project_id })} className="text-info hover:underline">{ch.head_number}</button>
                              {" "}<span className="font-semibold text-pos">+{fmt(ch.caught, currency)}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Rad 2 — Flagget per type (bred) + Status (smal) */}
            {typeStats.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                <div className="lg:col-span-2">
                  <div className="cx-card p-5 h-full">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[13px] font-semibold text-night">{t("analytics.dev_by_type")}</p>
                      <p className="text-[11.5px] text-faint">{t("analytics.filter_hint")}</p>
                    </div>
                    <div className="space-y-2">
                      {typeStats.map((s: any) => {
                        const max = typeStats[0].sum || 1; const active = devType === s.type;
                        return (
                          <button key={s.type} onClick={() => setDevType(active ? "all" : s.type)} className="w-full flex items-center gap-3 text-left group">
                            <span className={`w-32 sm:w-44 shrink-0 text-[12.5px] truncate ${active ? "font-semibold text-night" : "text-muted"}`}>{dispLabel(s.type)}</span>
                            <div className="flex-1 h-5 rounded-[6px] bg-black/[0.04] overflow-hidden"><div className="h-full rounded-[6px] transition-all" style={{ width: `${Math.max(4, (s.sum / max) * 100)}%`, background: LOGO_RED, opacity: active ? 1 : 0.82 }} /></div>
                            <span className="w-24 sm:w-28 shrink-0 text-right text-[12.5px] font-semibold tabular text-night">{fmt(s.sum, currency)}</span>
                            <span className="w-7 shrink-0 text-right text-[11.5px] text-faint tabular">{s.count}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-1">
                  <div className="cx-card p-5 h-full flex flex-col">
                    <p className="text-[13px] font-semibold text-night mb-4">{t("analytics.status_title")}</p>
                    <div className="flex-1 flex flex-col justify-center">
                      <div className="h-3 rounded-full bg-black/[0.06] overflow-hidden flex mb-4">
                        <div className="h-full bg-pos" style={{ width: `${deviations.length ? (resolvedCount / deviations.length) * 100 : 0}%` }} />
                        <div className="h-full bg-flag" style={{ width: `${deviations.length ? (openCount / deviations.length) * 100 : 0}%` }} />
                      </div>
                      <div className="space-y-2.5">
                        <button onClick={() => setDevStatus(devStatus === "resolved" ? "all" : "resolved")} className="w-full flex items-center justify-between text-[12.5px] group">
                          <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-pos" /><span className="text-muted group-hover:text-night">{t("analytics.resolved")}</span></span>
                          <span className="font-semibold tabular text-night">{resolvedCount}</span>
                        </button>
                        <button onClick={() => setDevStatus(devStatus === "open" ? "all" : "open")} className="w-full flex items-center justify-between text-[12.5px] group">
                          <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-flag" /><span className="text-muted group-hover:text-night">{t("analytics.open")}</span></span>
                          <span className="font-semibold tabular text-night">{openCount}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <p className="text-[12px] text-muted mb-2.5 leading-relaxed">{t("analytics.deviations_note")}</p>
            {/* Rent filterfelt: aktivt type-filter (fra grafen) + status-segment + antall */}
            <div className="flex items-center justify-between gap-3 mb-3 no-print flex-wrap">
              <div className="flex items-center gap-2 text-[12px]">
                {devType !== "all" ? (
                  <button onClick={() => setDevType("all")} className="inline-flex items-center gap-1.5 font-medium px-2.5 py-1 rounded-full bg-night text-white">
                    {dispLabel(devType)} <X className="w-3 h-3" />
                  </button>
                ) : <span className="text-faint">{t("analytics.filter_hint")}</span>}
                <span className="text-faint tabular">· {t("analytics.showing", { shown: filteredDev.length, total: deviations.length })}</span>
              </div>
              <div className="inline-flex items-center gap-0.5 bg-canvas border border-line rounded-[10px] p-0.5">
                {["all", "open", "resolved"].map(st => (
                  <button key={st} onClick={() => setDevStatus(st)} className={`text-[12px] font-medium px-3 py-1 rounded-[8px] transition-colors ${devStatus === st ? "bg-night text-white" : "text-muted hover:text-night"}`}>{st === "all" ? t("analytics.all") : t(`analytics.${st}`)}</button>
                ))}
              </div>
            </div>

            <div className="cx-card overflow-hidden">
              <div className="grid grid-cols-[1fr_120px_120px_90px_90px] gap-3 px-4 py-2.5 bg-panel/60 border-b border-line text-[10.5px] font-semibold uppercase text-faint tracking-wide">
                <div>{t("analytics.col_type")}</div><div>{t("analytics.col_invoice")}</div><div className="text-right">{t("analytics.col_flagged")}</div><div>{t("analytics.col_status")}</div><div className="text-right">{t("analytics.col_date")}</div>
              </div>
              {filteredDev.length === 0 ? <p className="text-[13px] text-faint text-center py-8">{t("analytics.no_match")}</p>
                : filteredDev.map((d: any, i: number) => <DeviationRow key={i} d={d} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
